import { snakeToCamel } from '@mint-lib/api';
import { Chat } from '@mint-sdk/api-ai-assistant';

import type { ContentBlock, MessageBlock, MessageGraph } from './types.js';

export const buildMessageGraph = (
  text: string,
  dataSource?: {
    graphs?: Record<string, Chat.Graph> | null;
  },
): MessageGraph => {
  const blocksMap = new Map<string, MessageBlock>();
  const blocksOrder: string[] = [];
  const outgoing = new Map<string, Set<string>>();
  const incoming = new Map<string, Set<string>>();

  const addRelation = (from: string, to: string) => {
    if (!outgoing.has(from)) outgoing.set(from, new Set());
    if (!incoming.has(to)) incoming.set(to, new Set());

    outgoing.get(from)?.add(to);
    incoming.get(to)?.add(from);
  };

  const addTextBlock = (text: string) => {
    const id = `text-${blocksOrder.length}`;
    blocksMap.set(id, {
      id,
      type: 'text',
      text,
    });
    blocksOrder.push(id);
  };

  const addContentBlock = (block: string) => {
    const contentBlock = parseBlock(block);
    const relatesTo = contentBlock?.varName;
    const id = `block-${blocksOrder.length}`;

    if (contentBlock.type === 'table') {
      blocksMap.set(id, {
        id,
        type: 'table',
        subtype: 'table',
        varName: contentBlock.varName,
      });
    }
    if (contentBlock.type === 'graph') {
      blocksMap.set(id, {
        id,
        type: 'graph',
        varName: contentBlock.varName,
        subtype:
          dataSource?.graphs?.[contentBlock.varName]?.graphType ?? 'graph',
      });
    }

    addRelation(id, relatesTo);
    blocksOrder.push(id);
  };

  const blocks = parseRichText(text);
  blocks.forEach((block) => {
    if (block.type === 'text') {
      addTextBlock(block.content);
    }
    if (block.type === 'block') {
      addContentBlock(block.content);
    }
  });

  return { relations: { outgoing, incoming }, blocksMap, blocksOrder };
};

const parseRichText = (input: string) => {
  const parts = [];

  const TAG_REGEX = /<(table|graph)\b[^>]*>/g;
  let lastIndex = 0;

  for (const match of input.matchAll(TAG_REGEX)) {
    const index = match.index;

    const textBlock = input.slice(lastIndex, index);

    if (textBlock.trim()) {
      parts.push({ type: 'text', content: textBlock });
    }

    parts.push({ type: 'block', content: match[0] });

    lastIndex = index + match[0].length;
  }

  const lastTextBlock = input.slice(lastIndex);

  if (lastTextBlock.trim()) {
    parts.push({ type: 'text', content: lastTextBlock });
  }

  return parts;
};

const parseBlock = (block: string) => {
  const el = new DOMParser().parseFromString(block, 'text/xml').documentElement;

  return {
    type: el.tagName,
    ...Object.fromEntries(
      [...el.attributes].map((a) => [
        // Table and chart names are sent in camelCase
        snakeToCamel(a.name),
        snakeToCamel(a.value),
      ]),
    ),
  } as ContentBlock;
};
